import { clsx as cn } from "clsx";

/**
 * The Batch & Bowl brand lockups.
 *
 * Geometry is transcribed from the identity sheet (Claude Design project
 * a28136c4, variant 6a "Air"); the palette lives in theme.css as the
 * `--brand-mark-*` tokens. Canonical vector source is
 * apps/web/public/brand/*.svg — those files are the export of record and must
 * not be hand-edited. `Brand.test.tsx` asserts the geometry below and the
 * theme.css tokens both still match them.
 *
 * The mark is inlined rather than loaded from public/ so that the primary
 * brand surface on every page paints with the first frame instead of popping
 * in on a second request.
 */

/** Sheet minimum: below 16px the mark stops resolving and you want the favicon cut. */
export const MARK_MIN_PX = 16;

/** Sheet minimum: the horizontal lockup goes stacked below this width. */
export const HORIZONTAL_LOCKUP_MIN_PX = 96;

type BrandTone = "auto" | "on-light" | "on-dark";

function toneClass(tone: BrandTone) {
  if (tone === "on-light") return "brand-on-light";
  if (tone === "on-dark") return "brand-on-dark";
  // "auto" inherits :root / .dark, so the mark follows the active theme.
  return undefined;
}

interface BrandMarkProps {
  /** Rendered edge in px. The sheet floors the mark at 16px. */
  size: number;
  /**
   * Which cut to paint. "auto" follows the theme; the explicit tones pin the
   * palette for grounds that are one colour regardless of theme.
   */
  tone?: BrandTone;
  className?: string;
  /**
   * Marks that sit next to the wordmark are decorative — the wordmark already
   * carries the accessible name, so repeating it would make screen readers say
   * "Batch and Bowl" twice. Standalone marks pass a title instead.
   */
  title?: string;
}

/**
 * The disc. Paths are verbatim from mark-primary.svg; only the fills are
 * tokenised. Do not rescale the viewBox — the sheet forbids stretching or
 * squashing the disc, and a non-uniform viewBox is exactly how that happens.
 */
export function BrandMark({ size, tone = "auto", className, title }: BrandMarkProps) {
  const decorative = title === undefined;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={cn(toneClass(tone), "shrink-0", className)}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? "true" : undefined}
      aria-label={title}
    >
      <circle cx="50" cy="50" r="49" fill="var(--brand-mark-disc)" />
      <circle
        cx="50"
        cy="50"
        r="41"
        fill="none"
        stroke="var(--brand-mark-line)"
        strokeWidth="2.5"
        opacity="0.45"
      />
      <path
        d="M50 46 C50 35 53 29 60 25"
        fill="none"
        stroke="var(--brand-mark-line)"
        strokeWidth="4.5"
        strokeLinecap="round"
      />
      <path d="M50 38 C42 38 37 33 37 27 C45 27 50 32 50 38 Z" fill="var(--brand-mark-leaf-front)" />
      <path d="M54 33 C54 26 60 21 67 21 C67 28 61 33 54 33 Z" fill="var(--brand-mark-leaf-back)" />
      <path d="M22 50 H78 A28 28 0 0 1 22 50 Z" fill="var(--brand-mark-bowl)" />
      <path d="M50 50 H78 A28 28 0 0 1 50 78 Z" fill="var(--brand-mark-bowl-shadow)" opacity="0.28" />
      <rect x="18" y="45" width="64" height="8" rx="4" fill="var(--brand-mark-rim)" />
    </svg>
  );
}

/**
 * "Batch & Bowl" set in the heading face. The spaces around the ampersand and
 * its sage colour are both from the sheet — the app previously shipped
 * "Batch&Bowl" with a terracotta ampersand in two different places.
 */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "font-heading whitespace-nowrap tracking-[-0.02em] leading-none",
        className,
      )}
    >
      Batch <span style={{ color: "var(--brand-amp)" }}>&amp;</span> Bowl
    </span>
  );
}

interface BrandLockupProps {
  /**
   * Sheet rules: horizontal is the default and floors at 96px wide; below that
   * use stacked. Reversed is the pill treatment for dark grounds.
   */
  variant?: "horizontal" | "stacked" | "reversed";
  tone?: BrandTone;
  /** Mark edge in px. Defaults follow the sheet's own lockup proportions. */
  markSize?: number;
  /** Wordmark size class. Defaults follow the sheet. */
  wordmarkClassName?: string;
  className?: string;
}

export function BrandLockup({
  variant = "horizontal",
  tone = "auto",
  markSize,
  wordmarkClassName,
  className,
}: BrandLockupProps) {
  // Sheet proportions: horizontal 132px mark / 34px wordmark, stacked 64/22,
  // reversed 56/21. Scaled down here to in-product defaults.
  const marks = { horizontal: 30, stacked: 44, reversed: 28 } as const;
  const words = {
    horizontal: "text-xl",
    stacked: "text-lg",
    reversed: "text-lg",
  } as const;

  // The tone class goes on the container, not the mark, so the wordmark
  // ampersand resolves against the same ground the disc does.
  const ground = variant === "reversed" ? "on-dark" : tone;
  const mark = <BrandMark size={markSize ?? marks[variant]} tone="auto" />;
  const word = (
    <Wordmark className={cn(words[variant], wordmarkClassName)} />
  );

  if (variant === "stacked") {
    return (
      <span
        role="img"
        aria-label="Batch & Bowl"
        className={cn(toneClass(ground), "inline-flex flex-col items-center gap-2.5", className)}
      >
        {mark}
        {word}
      </span>
    );
  }

  if (variant === "reversed") {
    return (
      <span
        role="img"
        aria-label="Batch & Bowl"
        className={cn(
          toneClass(ground),
          "inline-flex items-center gap-3 rounded-full bg-sage-800 py-3 pl-3 pr-[22px] text-primary-foreground",
          className,
        )}
      >
        {mark}
        {word}
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label="Batch & Bowl"
      className={cn(toneClass(ground), "inline-flex items-center gap-2.5", className)}
    >
      {mark}
      {word}
    </span>
  );
}
